import { createAsyncThunk } from "@reduxjs/toolkit";

import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { GetAllEmployees, GetAllEmployeesWithoutRestrictions, GetEmployeesWithFilters, GetActiveEmployees, GetActiveEmployeesWithDepartments, GetEmployeeByID, GetAllEmployeeDocuments, AddNewEmployeeDocument, DeleteDocument, GetAllEmployeeDevices, AddNewEmployee, UpdateEmployee, DeleteEmployee, UpdateEmployeeProfilePicture, GetEmployeeDetailsByID, CheckExistingUser, GetBirthday, UpdateFCMToken, ChangePassword } from "helpers/api_method";
import { handleUserStatusChange } from "helpers/userStatusHelper";
import { extractArrayFromResponse, extractDataFromResponse } from "utils/responseHelpers";

export const getAllUsers = createAsyncThunk("employees/getAllUsers", async (_, { rejectWithValue }) => {
    try {
        // Only return Active employees - use GetActiveEmployees instead of GetAllEmployees
        const response = await GetActiveEmployees();
        const data = extractArrayFromResponse(response);
        // Filter to ensure only Active status employees are returned
        const activeEmployees = data.filter((emp: any) => emp.status === 'Active' || emp.work_status === 'Active');
        return activeEmployees;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || error?.message || 'Failed to fetch employees');
    }
});

export const getAllUsersWithoutRestrictions = createAsyncThunk("employees/getAllUsersWithoutRestrictions", async (_, { rejectWithValue }) => {
    try {
        const response = await GetAllEmployeesWithoutRestrictions();
        const data = extractArrayFromResponse(response);
        return data;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || error?.message || 'Failed to fetch all employees');
    }
});



export const getEmployeesWithFilters = createAsyncThunk("employees/getEmployeesWithFilters", async (filters: any, { rejectWithValue }) => {
    try {
        // Ensure status filter defaults to Active if not specified
        const filtersWithActive = {
            ...filters,
            status: filters.status || 'Active'
        };
        const response = await GetEmployeesWithFilters(filtersWithActive);
        const data = extractArrayFromResponse(response);
        // Additional client-side filter to ensure only Active employees
        // (in case API doesn't filter properly)
        const filteredData = filtersWithActive.status === 'Active' 
            ? data.filter((emp: any) => emp.status === 'Active' || emp.work_status === 'Active')
            : data;
        return filteredData;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || error?.message || 'Failed to fetch filtered employees');
    }
});

export const getActiveUsers = createAsyncThunk("employees/getActiveUsers", async (_, { rejectWithValue }) => {
    try {
        const response = await GetActiveEmployees();
        const data = extractArrayFromResponse(response);
        return data;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || error?.message || 'Failed to fetch active employees');
    }
});

export const getActiveUsersWithDepartments = createAsyncThunk("employees/getActiveUsersWithDepartments", async (params: { page?: number; per_page?: number } | undefined = undefined, { rejectWithValue }) => {
    try {
        const response = await GetActiveEmployeesWithDepartments(params);
        // Handle both paginated and non-paginated responses
        const responseData = response?.data || response;
        if (responseData && typeof responseData === 'object' && 'users' in responseData) {
            // Paginated response
            return {
                users: responseData.users || [],
                pagination: responseData.pagination || null
            };
        } else {
            // Non-paginated response (backward compatible)
            const data = extractArrayFromResponse(response);
            return {
                users: data,
                pagination: null
            };
        }
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || error?.message || 'Failed to fetch active employees with departments');
    }
});
export const checkExistingEmployee = createAsyncThunk("employees/checkExistingEmployee", async (event: any, { rejectWithValue }) => {
    try {
        const response = await CheckExistingUser(event);
        return response;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || error?.message || 'Failed to check existing employee');
    }
});
export const getEmployeeDetailsByID = createAsyncThunk("employees/getEmployeeDetailsByID", async (event: any) => {
    try {
        const response = await GetEmployeeDetailsByID(event);
        const data = extractDataFromResponse(response);
        return data;
    } catch (error) {
        return error;
    }
});
export const getEmployeeDetailsByEmployeeID = createAsyncThunk("employees/getEmployeeDetailsByEmployeeID", async (event: any) => {
    try {
        const response = await GetEmployeeDetailsByID(event);
        const data = extractDataFromResponse(response);
        return data;
    } catch (error) {
        return error;
    }
});
export const addNewDEmployee = createAsyncThunk(
    "employees/addNewDEmployee",
    async (event: any, { rejectWithValue }) => {
    try {
        const response = await AddNewEmployee(event);
        toast.success("Employee created Successfully", { autoClose: 2000 });
        const data = extractDataFromResponse(response);
        return data;
    } catch (error: any) {
        // Let the caller decide how to display the error; normalize to a string message.
        const message =
            typeof error === "string"
                ? error
                : error?.response?.data?.message ||
                  error?.message ||
                  "Employee creation Failed";
        return rejectWithValue(message);
    }
});
export const updateExistingEmployee = createAsyncThunk("employees/updateExistingEmployee", async (event: any, { dispatch }) => {
    try {
        const response = await UpdateEmployee(event.id, event);
        
        // Check if user status is being changed from Active to something else
        if (event.status && event.status !== 'Active') {
            try {
                await handleUserStatusChange(event.id, event.status);
                // Also dispatch the calendar action to update the UI
                const { deleteEventsByUserId } = await import('../calendar/thunk');
                await dispatch(deleteEventsByUserId(event.id));
            } catch (calendarError) {
                console.error("Error deleting user events:", calendarError);
                // Don't fail the employee update if calendar deletion fails
            }
        }
        
        toast.success("User Details updated Successfully", { autoClose: 2000 });
        const data = extractDataFromResponse(response);
        return data;
    } catch (error) {
        toast.error("User Details update Failed", { autoClose: 2000 });
        return error;
    }
});
export const updateExistingPassword = createAsyncThunk("employees/UpdateExistingEmpFCMToken", async (event: any, { rejectWithValue }) => {
    try {
        const response = await ChangePassword(event);
        
        // Check if response has the expected structure
        if (response && response.data) {
            toast.success("Password updated successfully! Please logout and login again...", { autoClose: 2000 });
            return response.data;
        } else {
            // If response doesn't have expected structure, treat as success
            toast.success("Password updated successfully! Please logout and login again...", { autoClose: 2000 });
            return { success: true };
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Error updating your password! Please contact support!";
        toast.error(errorMessage, { autoClose: 2000 });
        return rejectWithValue(errorMessage);
    }
});
export const addNewUserProfilePicture = createAsyncThunk(
  "employees/addNewUserProfilePicture",
  async ({ employeeId, formData }: { employeeId: any; formData: FormData }, { rejectWithValue }) => {
    try {
      const response = await UpdateEmployeeProfilePicture(employeeId, formData);
      toast.success("Profile Picture added successfully", { autoClose: 2000 });
      const data = extractDataFromResponse(response);
      return data;
    } catch (error) {
      console.error('Profile picture upload error:', error);
      return rejectWithValue(error);
    }
  }
);
export const deleteExistingEmployee = createAsyncThunk("employees/deleteExistingEmployee", async (event: any) => {
    try {
        const response = await DeleteEmployee(event.id);
        toast.success("Employee deleted Successfully", { autoClose: 2000 });
        const data = extractDataFromResponse(response);
        return data;
    } catch (error) {
        toast.error("Employee deletion Failed", { autoClose: 2000 });
        return error;
    }
});

export const getAllUserDocuments = createAsyncThunk("employees/getAllUserDocuments", async (event: any) => {
    try {
        const response = await GetAllEmployeeDocuments(event);
        const data = extractArrayFromResponse(response);
        return data;
    } catch (error) {
        return error;
    }
});
export const addNewUserDocuments = createAsyncThunk(
  "employees/addNewUserDocuments",
  async ({ employeeId, formData }: { employeeId: any; formData: FormData }, { rejectWithValue }) => {
    try {
      // Call the API to upload the document
      const response = await AddNewEmployeeDocument(employeeId, formData);
      toast.success("Document added successfully", { autoClose: 2000 });
      const data = extractDataFromResponse(response);
      return data;
    } catch (error) {
      return error;
    }
  }
);
export const deleteUserDocuments = createAsyncThunk("employees/deleteUserDocuments", async (event: any) => {
    try {
        const response = await DeleteDocument(event);
        toast.success("Document deleted Successfully", { autoClose: 2000 });
        const data = extractDataFromResponse(response);
        return data;
    } catch (error) {
        return error;
    }
});

export const getAllUserDevices = createAsyncThunk("employees/getAllUserDevices", async (event: any, { rejectWithValue }) => {
    try {
        const response = await GetAllEmployeeDevices(event);
        const data = extractArrayFromResponse(response);
        if (data && data.length >= 0) {
            return data;
        } else {
            return rejectWithValue("Failed to fetch devices");
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch devices";
        return rejectWithValue(errorMessage);
    }
});

export const getBirthdays = createAsyncThunk("employees/getBirthdays", async () => {
    try {
        const response = await GetBirthday();
        const data = extractArrayFromResponse(response);
        return data;
    } catch (error) {
        return error;
    }
});

export const UpdateExistingEmpFCMToken = createAsyncThunk("employees/UpdateExistingEmpFCMToken", async (event: any) => {
    try {
        const response = await UpdateFCMToken(event.id, event);
        const data = extractDataFromResponse(response);
        return data;
    } catch (error) {
        return error;
    }
});


